import { CommonModule, Location, ViewportScroller } from '@angular/common';
import { Component, Input, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NavigationComponent } from '../../components/navigation/navigation.component';
import { AssessmentsService } from '../../services/assessments.service';
import { FgaData, FgaDataSet, initDataFGA } from '../../services/model';
import { getDateIsoString, getDateIsoStringPlus1Minute } from '../../services/util.functions';
import { AssessmentBaseComponent } from '../assessment-base/assessment-base.component';
import { DynamicTableInputBbsComponent } from '../bbs/dynamic-table-input-bbs/dynamic-table-input-bbs.component';

@Component({
  selector: 'app-fga',
  standalone: true,
  imports: [
    DynamicTableInputBbsComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatButtonModule,
    MatCardModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatChipsModule,
    MatSelectModule,
    MatTableModule,
    RouterModule,
    MatTooltipModule,
    MatIconModule,
    NavigationComponent,
  ],
  templateUrl: './fga.component.html',
  styleUrl: './fga.component.scss',
})
export class FgaComponent extends AssessmentBaseComponent implements OnInit {
  location: Location = inject(Location);
  @Input() assessmentId: string | undefined;
  protected readonly anleitung =
    'Beim FGA durchläuft der Patient einige Aufgaben, die mit einem Score von 0 - 3 bewertet werden.\n\n' + '\n\n';
  private assessmentService: AssessmentsService = inject(AssessmentsService);
  private scroller: ViewportScroller = inject(ViewportScroller);
  private fb: FormBuilder = inject(FormBuilder);
  assessmentData: WritableSignal<FgaData | undefined> = signal<FgaData | undefined>(undefined);
  assessmentDataNew: FgaData = JSON.parse(JSON.stringify(initDataFGA));
  hasTriedToSave = signal(false);
  hasError = signal({
    fga: false,
  });
  hasMissingInputs = signal({
    fga: true,
  });

  form: FormGroup = this.fb.group({
    assessmentTime: [getDateIsoStringPlus1Minute(), Validators.required],
    note: [''],
  });

  onFgaData(data: { data: Array<FgaDataSet>; note: string; hasError: boolean; hasMissingInputs: boolean }) {
    this.assessmentDataNew.fga.data = data.data;
    this.assessmentDataNew.fga.note = data.note;
    this.hasError.update(current => ({
      ...current,
      fga: data.hasError,
    }));
    this.hasMissingInputs.update(current => ({
      ...current,
      fga: data.hasMissingInputs,
    }));
  }

  ngOnInit(): void {
    this.scroller.scrollToPosition([0, 0]);

    if (this.assessmentId) {
      this.assessmentService.getAssessment(this.assessmentId).then(data => {
        // patch taskInfo
        const subData: FgaData = data.data as FgaData;

        // add manual
        subData.fga.manual = initDataFGA.fga.manual;

        const source = initDataFGA.fga.data;
        const target = subData.fga.data;
        for (let index = 0; index < target.length; index++) {
          const targetDS: FgaDataSet = target[index];
          const sourceDS: FgaDataSet = source[index];
          targetDS.taskInfo = sourceDS.taskInfo;
        }

        this.assessmentData.set(data.data as FgaData);
        const date = getDateIsoString(new Date(data.dateIso));
        this.form.controls['assessmentTime'].patchValue(date);

        let note = data.note;
        const hasNewNote = note && note.length > 0;
        if (!hasNewNote) {
          // try migration
          note = subData.fga.note;
          subData.fga.note = '';
        }
        this.form.controls['note'].patchValue(note);

        this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
      });
    } else {
      this.assessmentData.set(initDataFGA);
      this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
    }
  }
  onSave() {
    this.hasTriedToSave.set(true);
    if (this.hasError().fga || this.hasMissingInputs().fga) {
      return;
    }

    // TEMP! Einfach mal was berechnet...
    // @TODO ins backend schieben, prüfen was schädigung etc ist...
    // this.assessmentDataNew.fga.data = this.assessmentDataNew.fga.data.map(row => {
    //   const value = (row as FgaDataSet).value ?? 0;
    //   const score = ((100 / 3) * value) / 100;
    //   row.score = 1 - score;
    //   return row;
    // });
    // const sum = this.assessmentDataNew.fga.data.reduce((sum, current) => {
    //   const value = (current as FgaDataSet).value ?? 0;
    //   return sum + value;
    // }, 0);

    // const score = ((100 / (10 * 3)) * sum) / 100;
    // this.assessmentDataNew.fga.score = 1 - score;

    if (this.assessmentId) {
      this.assessmentService
        .updateAssessment(this.assessmentId, {
          type: 'FGA',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then(res => {
          this.router.navigate(['../../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'FGA', dId: this.assessmentId },
          });
        });
    } else {
      this.assessmentService
        .createAssessment({
          type: 'FGA',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then((docRef: DocumentReference) => {
          this.router.navigate(['../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'FGA', dId: docRef.id },
          });
        });
    }
  }

  onNavigateBack() {
    if (this.assessmentId) {
      // this.router.navigate(['../../../reports/assessments'], { relativeTo: this.route });
      this.location.back();
    } else {
      // this.router.navigate(['../../reports/assessments'], { relativeTo: this.route });
      this.router.navigate(['../../assessments/new'], { relativeTo: this.route });
    }
  }
}
